/**
 * @file useToast.ts
 * @description Hook for managing toast notifications
 * @author XiaVan Team
 * 
 * Usage:
 * const { showToast } = useToast()
 * showToast({ type: 'success', title: 'Usuario eliminado', message: 'El usuario ha sido eliminado correctamente' })
 */

import { create } from 'zustand'
import type { Toast } from '@/components/ui/Toast'

interface ToastStore {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  clearAll: () => void
}

/**
 * Zustand store for toast notifications
 */
export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  
  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random()}`
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))
  },
  
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
  
  clearAll: () => set({ toasts: [] }),
}))

/**
 * Hook for showing toast notifications
 * 
 * @returns Object with showToast function
 * 
 * @example
 * const { showToast } = useToast()
 * 
 * // Success
 * showToast({ type: 'success', title: 'Éxito', message: 'Operación completada' })
 * 
 * // Error
 * showToast({ type: 'error', title: 'Error', message: 'No se pudo completar' })
 * 
 * // Warning
 * showToast({ type: 'warning', title: 'Atención', message: 'Verifica los datos' })
 * 
 * // Info
 * showToast({ type: 'info', title: 'Información', message: 'Proceso iniciado' })
 */
export function useToast() {
  const addToast = useToastStore((state) => state.addToast)

  return {
    showToast: addToast,
  }
}
